import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

/**
 * Elements a keyboard user can land on. Deliberately does NOT filter by visibility
 * (`offsetParent`/`getBoundingClientRect`) — jsdom performs no layout, so every element
 * always reports itself as hidden by those checks, which would break focus handling under
 * test. In production the modal has nothing conditionally hidden inside the trapped
 * container, so the extra filtering wouldn't buy anything here anyway.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * While `active` is true: focuses the first focusable element inside `containerRef` and traps
 * `Tab`/`Shift+Tab` so focus cycles within the container instead of escaping into the host
 * page. Listeners are removed as soon as `active` goes false (or the component unmounts) —
 * never left attached to a container that's no longer supposed to be trapping focus.
 *
 * Guarded for jsdom / non-browser environments: bails out (no-op) if `document` isn't
 * available, and every DOM call it makes (`querySelectorAll`, `.focus()`) is standard jsdom
 * behavior with no unsupported APIs involved.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;

    const focusables = getFocusable(container);
    focusables[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const items = getFocusable(container);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const first = items[0]!;
      const last = items[items.length - 1]!;
      const activeElement = document.activeElement;
      // `container.contains(container)` is true, and the dialog itself is focusable
      // (`tabindex="-1"`, so a click on its dead space lands here rather than on
      // `document.body` — see `Modal.tsx`). Treating that as "inside" would let Shift+Tab from
      // the container escape the trap entirely, since the container is neither `first` nor
      // `last`. Treating it as outside wraps to the correct end instead.
      const activeIsInside =
        activeElement instanceof Node && activeElement !== container && container.contains(activeElement);

      if (event.shiftKey) {
        if (!activeIsInside || activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!activeIsInside || activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, containerRef]);
}
